export type NoiseType = 'white' | 'pink' | 'brown';

export interface FrequencyBand {
  name: string;
  minFreq: number;
  maxFreq: number;
  gain: number; // Now in dB scale: -12 to +12
}

export interface NoiseSettings {
  noiseType: NoiseType;
  frequencyBands: FrequencyBand[];
  volume: number;
  duration: number;
}

// Default frequency bands with 0dB gain (flat response)
export const DEFAULT_FREQUENCY_BANDS: FrequencyBand[] = [
  { name: 'Sub-Bass', minFreq: 20, maxFreq: 60, gain: 0 },
  { name: 'Bass', minFreq: 60, maxFreq: 250, gain: 0 },
  { name: 'Low-Mid', minFreq: 250, maxFreq: 500, gain: 0 },
  { name: 'Mid', minFreq: 500, maxFreq: 1000, gain: 0 },
  { name: 'High-Mid', minFreq: 1000, maxFreq: 2000, gain: 0 },
  { name: 'Presence', minFreq: 2000, maxFreq: 4000, gain: 0 },
  { name: 'Low-Treble', minFreq: 4000, maxFreq: 6000, gain: 0 },
  { name: 'Mid-Treble', minFreq: 6000, maxFreq: 10000, gain: 0 },
  { name: 'High-Treble', minFreq: 10000, maxFreq: 16000, gain: 0 },
  { name: 'Air', minFreq: 16000, maxFreq: 20000, gain: 0 }
];

// Preset EQ curves
export const PRESET_EQ_CURVES = {
  white: [
    { name: 'Sub-Bass', minFreq: 20, maxFreq: 60, gain: 0 },
    { name: 'Bass', minFreq: 60, maxFreq: 250, gain: 0 },
    { name: 'Low-Mid', minFreq: 250, maxFreq: 500, gain: 0 },
    { name: 'Mid', minFreq: 500, maxFreq: 1000, gain: 0 },
    { name: 'High-Mid', minFreq: 1000, maxFreq: 2000, gain: 0 },
    { name: 'Presence', minFreq: 2000, maxFreq: 4000, gain: 0 },
    { name: 'Low-Treble', minFreq: 4000, maxFreq: 6000, gain: 0 },
    { name: 'Mid-Treble', minFreq: 6000, maxFreq: 10000, gain: 0 },
    { name: 'High-Treble', minFreq: 10000, maxFreq: 16000, gain: 0 },
    { name: 'Air', minFreq: 16000, maxFreq: 20000, gain: 0 }
  ], // Flat response
  pink: [
    { name: 'Sub-Bass', minFreq: 20, maxFreq: 60, gain: 0 },
    { name: 'Bass', minFreq: 60, maxFreq: 250, gain: -2 },
    { name: 'Low-Mid', minFreq: 250, maxFreq: 500, gain: -4 },
    { name: 'Mid', minFreq: 500, maxFreq: 1000, gain: -6 },
    { name: 'High-Mid', minFreq: 1000, maxFreq: 2000, gain: -8 },
    { name: 'Presence', minFreq: 2000, maxFreq: 4000, gain: -10 },
    { name: 'Low-Treble', minFreq: 4000, maxFreq: 6000, gain: -12 },
    { name: 'Mid-Treble', minFreq: 6000, maxFreq: 10000, gain: -14 },
    { name: 'High-Treble', minFreq: 10000, maxFreq: 16000, gain: -16 },
    { name: 'Air', minFreq: 16000, maxFreq: 20000, gain: -18 }
  ],
  brown: [
    { name: 'Sub-Bass', minFreq: 20, maxFreq: 60, gain: 6 },
    { name: 'Bass', minFreq: 60, maxFreq: 250, gain: 2 },
    { name: 'Low-Mid', minFreq: 250, maxFreq: 500, gain: -4 },
    { name: 'Mid', minFreq: 500, maxFreq: 1000, gain: -10 },
    { name: 'High-Mid', minFreq: 1000, maxFreq: 2000, gain: -16 },
    { name: 'Presence', minFreq: 2000, maxFreq: 4000, gain: -20 },
    { name: 'Low-Treble', minFreq: 4000, maxFreq: 6000, gain: -24 },
    { name: 'Mid-Treble', minFreq: 6000, maxFreq: 10000, gain: -28 },
    { name: 'High-Treble', minFreq: 10000, maxFreq: 16000, gain: -32 },
    { name: 'Air', minFreq: 16000, maxFreq: 20000, gain: -36 }
  ]
};

export class WhiteNoiseGeneratorNode {
  private audioContext: BaseAudioContext;
  private gainNode: GainNode;
  private noiseNode: AudioWorkletNode | ScriptProcessorNode | null = null;
  private filters: BiquadFilterNode[] = [];
  private settings: NoiseSettings;
  private isPlaying: boolean = false;
  private startTime: number = 0;
  private stopTime: number = 0;
  private stopTimer: number | null = null;
  private static workletLoadedContexts = new WeakSet<BaseAudioContext>();

  constructor(audioContext: BaseAudioContext, settings: NoiseSettings) {
    this.audioContext = audioContext;
    // Deep copy settings to avoid mutating the original
    this.settings = {
      ...settings,
      frequencyBands: settings.frequencyBands.map(band => ({ ...band }))
    };
    
    // Create main gain node
    this.gainNode = audioContext.createGain();
    this.gainNode.gain.value = settings.volume;
    
    // Setup frequency band filters
    this.setupFilters();
  }

  private static async loadWorklet(audioContext: BaseAudioContext): Promise<boolean> {
    // Check if we've already loaded the worklet for this context
    if (WhiteNoiseGeneratorNode.workletLoadedContexts.has(audioContext)) {
      return true;
    }
    
    try {
      // Only AudioContext (not OfflineAudioContext) has audioWorklet
      if ('audioWorklet' in audioContext) {
        await (audioContext as AudioContext).audioWorklet.addModule('/worklets/white-noise-processor.js');
        WhiteNoiseGeneratorNode.workletLoadedContexts.add(audioContext);
        return true;
      }
    } catch (error) {
      console.warn('Failed to load AudioWorklet, falling back to ScriptProcessor:', error);
    }
    
    return false;
  }

  private async createNoiseSource(): Promise<AudioWorkletNode | ScriptProcessorNode> {
    // Try to use AudioWorklet first (only available in AudioContext, not OfflineAudioContext)
    if ('audioWorklet' in this.audioContext && WhiteNoiseGeneratorNode.workletLoadedContexts.has(this.audioContext)) {
      try {
        const workletNode = new AudioWorkletNode(this.audioContext as AudioContext, 'white-noise-processor');
        return workletNode;
      } catch (error) {
        console.warn('Failed to create AudioWorkletNode:', error);
      }
    }
    
    // Fallback to ScriptProcessorNode
    const bufferSize = 4096;
    const scriptNode = this.audioContext.createScriptProcessor(bufferSize, 0, 2);
    
    scriptNode.onaudioprocess = (event) => {
      const output = event.outputBuffer;
      
      for (let channel = 0; channel < output.numberOfChannels; channel++) {
        const outputData = output.getChannelData(channel);
        for (let i = 0; i < outputData.length; i++) {
          outputData[i] = Math.random() * 2 - 1;
        }
      }
    };
    
    return scriptNode;
  }

  private setupFilters(): void {
    // Clear existing filters
    this.filters.forEach(filter => filter.disconnect());
    this.filters = [];
    
    // Create a series of EQ filters
    this.settings.frequencyBands.forEach((band, index) => {
      const filter = this.audioContext.createBiquadFilter();
      
      // First band: lowshelf, last band: highshelf, others: peaking
      if (index === 0) {
        filter.type = 'lowshelf';
        filter.frequency.value = band.maxFreq;
      } else if (index === this.settings.frequencyBands.length - 1) {
        filter.type = 'highshelf';
        filter.frequency.value = band.minFreq;
      } else {
        filter.type = 'peaking';
        const centerFreq = Math.sqrt(band.minFreq * band.maxFreq);
        filter.frequency.value = centerFreq;
        // Use a moderate Q for smooth EQ response
        filter.Q.value = 1.0;
      }
      
      // Set gain in dB
      filter.gain.value = band.gain;
      
      // Store reference
      this.filters.push(filter);
    });
  }

  connect(destination: AudioNode): void {
    this.gainNode.connect(destination);
  }

  disconnect(): void {
    this.gainNode.disconnect();
    this.filters.forEach(filter => filter.disconnect());
  }

  async start(when: number = 0): Promise<void> {
    if (this.isPlaying) return;
    
    // Calculate delay time
    const delayTime = Math.max(0, when - this.audioContext.currentTime);
    
    // If we need to delay the start, wait before creating the noise source
    if (delayTime > 0 && 'audioWorklet' in this.audioContext) {
      // For regular AudioContext, use setTimeout to delay the actual start
      this.stopTimer = window.setTimeout(async () => {
        await this.actualStart(when);
      }, delayTime * 1000);
      return;
    }
    
    // For immediate start or OfflineAudioContext, start right away
    await this.actualStart(when);
  }
  
  private async actualStart(when: number): Promise<void> {
    // Try to load worklet, but don't fail if it doesn't work
    try {
      await WhiteNoiseGeneratorNode.loadWorklet(this.audioContext);
    } catch (error) {
      console.warn('Could not load AudioWorklet, will use ScriptProcessor:', error);
    }
    
    // Create noise source
    this.noiseNode = await this.createNoiseSource();
    
    // Connect source through filters in series
    let previousNode: AudioNode = this.noiseNode;
    
    // Connect through each filter in sequence
    this.filters.forEach(filter => {
      previousNode.connect(filter);
      previousNode = filter;
    });
    
    // Connect the last filter to the gain node
    previousNode.connect(this.gainNode);
    
    // Schedule stop time if duration is set
    if (this.settings.duration > 0) {
      this.stopTime = when + this.settings.duration;
      
      // For OfflineAudioContext, we need to handle stopping differently
      if (!('audioWorklet' in this.audioContext)) {
        // For offline context, don't override gain automation - let fades handle it
        // Only add a final stop ramp if there's no fade out scheduled
        // This will be handled by the fade scheduling methods
      } else {
        // For regular AudioContext, schedule a timer to stop
        // We're already at the start time now, so just use duration
        const timeUntilStop = this.settings.duration * 1000;
        if (timeUntilStop > 0) {
          // Clear any existing timer first
          if (this.stopTimer) {
            window.clearTimeout(this.stopTimer);
          }
          this.stopTimer = window.setTimeout(() => {
            this.stop();
          }, timeUntilStop);
        }
      }
    }
    
    this.isPlaying = true;
    this.startTime = when || this.audioContext.currentTime;
  }

  stop(): void {
    if (!this.isPlaying || !this.noiseNode) return;
    
    // Clear the stop timer if it exists
    if (this.stopTimer) {
      window.clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }
    
    this.noiseNode.disconnect();
    
    // Clean up ScriptProcessorNode if that's what we're using
    if ('onaudioprocess' in this.noiseNode) {
      (this.noiseNode as ScriptProcessorNode).onaudioprocess = null;
    }
    
    this.noiseNode = null;
    this.isPlaying = false;
  }

  setVolume(volume: number): void {
    this.gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
  }

  scheduleFadeIn(duration: number, targetVolume: number, startTime?: number): void {
    const when = startTime ?? this.audioContext.currentTime;
    // Cancel any previous automation to avoid conflicts
    this.gainNode.gain.cancelScheduledValues(when);
    // Start from silence
    this.gainNode.gain.setValueAtTime(0, when);
    // Ramp up to target volume over duration
    this.gainNode.gain.linearRampToValueAtTime(targetVolume, when + duration);
    // Set the final volume to be maintained after fade
    this.gainNode.gain.setValueAtTime(targetVolume, when + duration);
  }

  scheduleFadeOut(duration: number, startTime?: number, currentVolume?: number): void {
    const when = startTime ?? this.audioContext.currentTime;
    const volumeToUse = currentVolume ?? this.settings.volume;
    // Don't cancel previous values, just set from the fade out point
    // Maintain current volume until fade out starts
    this.gainNode.gain.setValueAtTime(volumeToUse, when);
    // Ramp down to silence over duration
    this.gainNode.gain.linearRampToValueAtTime(0, when + duration);
    
    // For offline context, ensure the track stops completely at the end
    if (!('audioWorklet' in this.audioContext) && this.settings.duration > 0) {
      // Schedule final silence after fade completes
      this.gainNode.gain.setValueAtTime(0, this.stopTime);
    }
  }

  updateBandGain(bandIndex: number, gain: number): void {
    if (bandIndex >= 0 && bandIndex < this.filters.length) {
      // Update the filter gain (in dB)
      this.filters[bandIndex].gain.setValueAtTime(gain, this.audioContext.currentTime);
      // Update our settings to keep track
      this.settings.frequencyBands[bandIndex].gain = gain;
    }
  }
  
  updateAllBands(bands: FrequencyBand[]): void {
    bands.forEach((band, index) => {
      if (index < this.filters.length) {
        this.filters[index].gain.setValueAtTime(band.gain, this.audioContext.currentTime);
        this.settings.frequencyBands[index].gain = band.gain;
      }
    });
  }

  updateSettings(settings: Partial<NoiseSettings>): void {
    if (settings.volume !== undefined) {
      this.setVolume(settings.volume);
    }
    
    if (settings.frequencyBands) {
      // Update all frequency bands
      settings.frequencyBands.forEach((band, index) => {
        this.updateBandGain(index, band.gain);
      });
      this.settings.frequencyBands = [...settings.frequencyBands];
    }
  }

  getElapsedTime(): number {
    if (!this.isPlaying) return 0;
    return this.audioContext.currentTime - this.startTime;
  }
}